import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArticleIcon from "@mui/icons-material/Article";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import { useAuthStore } from "../../../../../../store/useAuthStore";
import { useUserStore } from "../../../../../../store/useUserStore";
import { useToast } from "../../../../../../hooks/useToast";
import {
  getSupportingDocuments,
  uploadSupportingDocument,
  getDocument,
  deleteDocument,
} from "../../../../../../services/dashboard";

type SupportingDocumentsSectionProps = {
  claimId: string;
  iceClaimNumber: string;
};

type SupportingDoc = {
  id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  category: string;
  created_by?: string;
  created_on?: string;
};

const CATEGORIES = [
  {
    key: "DECEASED_VERIFICATION",
    title: "Deceased Verification",
    description: "Deceased verification report (Verix Check) required for every death claim assessment.",
    icon: <CheckCircleIcon sx={{ color: "#10b981", fontSize: 32 }} />,
  },
  {
    key: "COMMUNICATIONS",
    title: "Communications (Comms)",
    description: "Claim-related communications: emails, letters, affidavits, and customer correspondence.",
    icon: <EmailIcon sx={{ color: "#3b82f6", fontSize: 32 }} />,
  },
  {
    key: "SUPPORTING_OTHER",
    title: "Other Documents",
    description: "Additional supporting evidence, affidavits, legal files, and miscellaneous claim documentation.",
    icon: <ArticleIcon sx={{ color: "#f97316", fontSize: 32 }} />,
  },
];

const ALLOWED_EXTENSIONS = ["pdf", "docx", "xlsx", "jpg", "png", "jpeg", "doc", "xls"];

export default function SupportingDocumentsSection({ claimId, iceClaimNumber }: SupportingDocumentsSectionProps) {
  const { userId } = useAuthStore();
  const role = useUserStore((state) => state.role);
  const toast = useToast();

  const [documents, setDocuments] = useState<SupportingDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState<string | null>(null);

  // Document Preview State
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<SupportingDoc | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewContentType, setPreviewContentType] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const previewUrlRef = useRef<string | null>(null);
  const previewRequestIdRef = useRef(0);

  const isAuthorizedToUpload = role === "claim_administrator" || role === "claim_manager";
  const isAuthorizedToView = role === "claim_administrator" || role === "claim_manager" || role === "underwriter";

  const fetchDocuments = async () => {
    if (!claimId || !userId) return;
    setLoading(true);
    try {
      const res = await getSupportingDocuments(claimId, userId);
      if (res && res.status === 200) {
        setDocuments(res.data || []);
      } else {
        console.error("Failed to load supporting documents:", res);
      }
    } catch (err) {
      console.error("Error loading supporting documents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorizedToView) {
      fetchDocuments();
    }
  }, [claimId, userId, role]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handleFileUpload = async (category: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      toast.showErrorToast("Unsupported file format! Please upload PDF, DOCX, XLSX, JPG, or PNG files.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.showErrorToast("File size cannot exceed 5MB.");
      return;
    }

    setUploadingCategory(category);
    try {
      const res = await uploadSupportingDocument(claimId, userId || "", category, file);
      if (res && res.status === 200) {
        toast.showSuccessToast("Document uploaded successfully!");
        fetchDocuments();
      } else {
        const errorMsg = res?.data?.message || "Failed to upload document.";
        toast.showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.showErrorToast("An error occurred during file upload.");
    } finally {
      setUploadingCategory(null);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      const res = await deleteDocument(iceClaimNumber, docId);
      if (res && (res.status === 200 || res.status === 204)) {
        toast.showSuccessToast("Document deleted successfully!");
        fetchDocuments();
      } else {
        toast.showErrorToast("Failed to delete document.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.showErrorToast("An error occurred while deleting the document.");
    }
  };

  const clearPreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const fetchDocumentBlob = async (doc: SupportingDoc): Promise<Blob | null> => {
    if (!iceClaimNumber || !doc.id) {
      toast.showErrorToast("Claim number or document ID is missing.");
      return null;
    }

    const res = await getDocument(iceClaimNumber, doc.id);
    if (res?.status !== 200 || !(res.data instanceof Blob)) {
      toast.showErrorToast("Unable to load the document. Please try again.");
      return null;
    }

    return res.data.type
      ? res.data
      : new Blob([res.data], { type: doc.file_type || "application/octet-stream" });
  };

  const handleOpenPreview = async (doc: SupportingDoc) => {
    const requestId = ++previewRequestIdRef.current;
    clearPreviewUrl();
    setSelectedDoc(doc);
    setOpenPreview(true);
    setPreviewLoading(true);
    setPreviewError("");

    try {
      const blob = await fetchDocumentBlob(doc);
      if (!blob) {
        if (requestId === previewRequestIdRef.current) {
          setPreviewError("The document could not be loaded for preview.");
        }
        return;
      }

      if (requestId !== previewRequestIdRef.current) return;
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
      setPreviewContentType(blob.type || doc.file_type || "application/octet-stream");
    } catch (err) {
      console.error("Preview error:", err);
      if (requestId === previewRequestIdRef.current) {
        setPreviewError("The document could not be loaded for preview.");
        toast.showErrorToast("An error occurred while loading the document.");
      }
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  const handleClosePreview = () => {
    previewRequestIdRef.current += 1;
    clearPreviewUrl();
    setSelectedDoc(null);
    setOpenPreview(false);
    setPreviewContentType("");
    setPreviewError("");
  };

  const handleDownload = async (doc: SupportingDoc | null) => {
    if (!doc) return;

    try {
      const blob = await fetchDocumentBlob(doc);
      if (!blob) return;

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = doc.file_name || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
    } catch (err) {
      console.error("Download error:", err);
      toast.showErrorToast("An error occurred while downloading the document.");
    }
  };

  const formatTimestamp = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-ZA", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const isImage = previewContentType.startsWith("image/")
    || Boolean(selectedDoc?.file_name?.match(/\.(jpg|jpeg|png|gif)$/i));
  const isPdf = previewContentType === "application/pdf"
    || Boolean(selectedDoc?.file_name?.match(/\.pdf$/i));

  if (!isAuthorizedToView) {
    return null;
  }

  return (
    <Box sx={{ mt: 5, mb: 4 }}>
      {/* Title Header with Accent Line */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: "#0f172a",
            letterSpacing: "-0.5px",
            textTransform: "uppercase",
            fontSize: "20px",
          }}
        >
          Supporting Documents Audit Trail
        </Typography>
        <Box
          sx={{
            width: "60px",
            height: "4px",
            backgroundColor: "#ff7a00",
            margin: "8px auto 0",
            borderRadius: "2px",
          }}
        />
      </Box>

      {loading && documents.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={30} sx={{ color: "#ff7a00" }} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {CATEGORIES.map((cat) => {
            const catDocs = documents.filter((d) => d.category === cat.key);
            const isUploading = uploadingCategory === cat.key;

            return (
              <Grid size={{ xs: 12, md: 4 }} key={cat.key}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.03)",
                    transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0px 12px 20px -3px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.03)",
                      borderColor: "#cbd5e1",
                    },
                    minHeight: "380px",
                  }}
                >
                  {/* Category Header */}
                  <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    {cat.icon}
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", fontSize: "16px" }}>
                      {cat.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 3, minHeight: "40px", fontSize: "13px" }}>
                    {cat.description}
                  </Typography>

                  <Divider sx={{ mb: 2.5 }} />

                  {/* Documents List */}
                  <Box sx={{ flexGrow: 1, overflowY: "auto", maxHeight: "200px", pr: 0.5 }}>
                    {catDocs.length === 0 ? (
                      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                        <DescriptionIcon sx={{ color: "#cbd5e1", fontSize: 40, mb: 1 }} />
                        <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic", textAlign: "center" }}>
                          No documents uploaded yet
                        </Typography>
                      </Box>
                    ) : (
                      catDocs.map((doc) => (
                        <Box
                          key={doc.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            mb: 1.5,
                            borderRadius: "10px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #f1f5f9",
                            transition: "background-color 0.2s",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1, mr: 1.5 }}>
                            <Tooltip title={doc.file_name} arrow placement="top">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: "#334155",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: "13px",
                                }}
                              >
                                {doc.file_name}
                              </Typography>
                            </Tooltip>
                            <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5, gap: 0.25 }}>
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11px", fontWeight: 500 }}>
                                By: {doc.created_by || "System"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "10px" }}>
                                {formatTimestamp(doc.created_on)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Button
                              size="small"
                              onClick={() => handleOpenPreview(doc)}
                              sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                color: "#2563eb",
                                fontWeight: 700,
                                minWidth: "auto",
                                p: "4px 8px",
                                borderRadius: "6px",
                                "&:hover": { backgroundColor: "rgba(37, 99, 235, 0.08)" },
                              }}
                            >
                              View
                            </Button>
                            {isAuthorizedToUpload && (
                              <Tooltip title="Delete Document" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(doc.id)}
                                  sx={{
                                    color: "#ef4444",
                                    p: "4px",
                                    borderRadius: "6px",
                                    "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>

                  {/* Upload Button */}
                  {isAuthorizedToUpload && (
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        component="label"
                        fullWidth
                        disabled={isUploading}
                        startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                        sx={{
                          backgroundColor: "#ff7a00",
                          color: "#fff",
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 700,
                          py: 1.25,
                          fontSize: "14px",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#e06c00",
                            boxShadow: "none",
                          },
                        }}
                      >
                        {isUploading ? "Uploading..." : "Upload Document"}
                        <input
                          type="file"
                          hidden
                          onChange={(e) => handleFileUpload(cat.key, e.target.files)}
                          accept=".pdf,.docx,.xlsx,.jpg,.png,.jpeg,.doc,.xls"
                        />
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Direct In-Screen Document Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "18px", color: "#0f172a" }}>
          Document Preview: {selectedDoc?.file_name}
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
              <CircularProgress size={30} sx={{ color: "#ff7a00" }} />
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Loading document...
              </Typography>
            </Box>
          ) : previewError ? (
            <Box display="flex" flexDirection="column" alignItems="center" p={4} textAlign="center">
              <DescriptionIcon sx={{ color: "#cbd5e1", fontSize: 60, mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#475569" }}>
                {previewError}
              </Typography>
            </Box>
          ) : selectedDoc && previewUrl ? (
            isImage ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <img
                  src={previewUrl}
                  alt={selectedDoc.file_name}
                  style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain", borderRadius: "8px" }}
                />
              </Box>
            ) : isPdf ? (
              <object
                data={previewUrl}
                type="application/pdf"
                width="100%"
                height="600px"
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={4} textAlign="center">
                  <DescriptionIcon sx={{ color: "#cbd5e1", fontSize: 60, mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#475569" }}>
                    PDF preview is not supported directly in this browser.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => handleDownload(selectedDoc)}
                    sx={{ mt: 2, backgroundColor: "#ff7a00", "&:hover": { backgroundColor: "#e06c00" }, textTransform: "none" }}
                  >
                    Download & Open Document
                  </Button>
                </Box>
              </object>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" p={4} textAlign="center">
                <DescriptionIcon sx={{ color: "#cbd5e1", fontSize: 60, mb: 2 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#475569" }}>
                  This file type cannot be previewed in the browser.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleDownload(selectedDoc)}
                  sx={{ mt: 2, backgroundColor: "#ff7a00", "&:hover": { backgroundColor: "#e06c00" }, textTransform: "none" }}
                >
                  Download & Open Document
                </Button>
              </Box>
            )
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={30} sx={{ color: "#ff7a00" }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              backgroundColor: "#64748b",
              "&:hover": { backgroundColor: "#475569" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
